import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, 
  User, Clock, Briefcase, MessageSquare, Trash2, X, Search, Check, Edit3
} from 'lucide-react';
import { crmDataService } from '../services/crmDataService';

interface UnifiedCalendarProps {
  currentUser: any;
  globalRefreshKey?: number;
}

const UnifiedCalendar: React.FC<UnifiedCalendarProps> = ({ currentUser, globalRefreshKey }) => {
  // =============================================================================
  // [1] 상태 관리 (State Management)
  // =============================================================================
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]); // 자동완성용 전체 거래처
  const [users, setUsers] = useState<any[]>([]);      // 자동완성용 전체 유저
  const [isLoading, setIsLoading] = useState(true);
  
  const [selectedDay, setSelectedDay] = useState<number | null>(new Date().getDate());
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
  const [clientHistory, setClientHistory] = useState<any[]>([]);

  // 📝 메모 즉시 수정을 위한 로컬 상태
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [tempMemo, setTempMemo] = useState('');

  // 모달 및 폼 데이터
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null, 
    team: '영업팀', 
    assignee: currentUser?.name || '',
    client_id: null as number | null, 
    client_name: '', 
    schedule_date: '', 
    task_type: '', // ✍️ 텍스트 입력방식 유지
    memo: ''
  });

  // 자동완성 제어 상태
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyList, setShowCompanyList] = useState(false);
  const companyListRef = useRef<HTMLDivElement>(null);

  // =============================================================================
  // [2] 데이터 로드 로직 (Data Fetching)
  // =============================================================================
  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const [scheduleData, contactData, userData] = await Promise.all([
        crmDataService.getSharedSchedules(year, month),
        crmDataService.getContacts(),
        crmDataService.getAllUsers()
      ]);
      
      setSchedules(scheduleData || []);
      setContacts(contactData || []);
      setUsers(userData || []);
    } catch (error) {
      console.error("데이터 로드 실패:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [currentDate, globalRefreshKey]);

  // 클릭 시 외부 영역 감지 (자동완성 리스트 닫기)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (companyListRef.current && !companyListRef.current.contains(e.target as Node)) {
        setShowCompanyList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 우측 히스토리 창: 선택된 거래처의 통합 히스토리 불러오기
  useEffect(() => {
    if (selectedSchedule?.client_name) {
      crmDataService.api_get_client_history_integrated('0', selectedSchedule.client_name)
        .then((res: any) => { 
          setClientHistory(res.deal_history || []);
        })
        .catch(err => {
          console.error("히스토리 로드 실패:", err);
          setClientHistory([]);
        });
    } else {
      setClientHistory([]);
    }
    // 상세창 변경 시 수정 모드 초기화
    setIsEditingMemo(false);
  }, [selectedSchedule]);

  // =============================================================================
  // [3] 달력 계산 및 헬퍼 함수
  // =============================================================================
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= lastDate; i++) days.push(i);
    return days;
  }, [currentDate]);

  const changeMonth = (offset: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));
    setSelectedDay(null);
    setSelectedSchedule(null);
  };

  // =============================================================================
  // [4] 이벤트 핸들러 (Event Handlers)
  // =============================================================================
  const handleSave = async () => {
    if (!formData.client_name || !formData.schedule_date) {
      return alert("거래처명과 날짜를 입력하세요.");
    }

    const submitData = {
      ...formData,
      client_id: formData.client_id && !isNaN(Number(formData.client_id)) 
                 ? Number(formData.client_id) 
                 : null
    };

    const res = await crmDataService.saveSharedSchedule(submitData);
    if (res.status === 'success') {
      setIsModalOpen(false);
      loadInitialData();
      alert("일정이 저장되었습니다.");
    } else {
      alert("저장에 실패했습니다: " + res.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("이 일정을 삭제하시겠습니까?\n삭제 시 히스토리 기록도 함께 삭제됩니다.")) return;
    const res = await crmDataService.deleteSharedSchedule(id);
    if (res.status === 'success') {
      setSelectedSchedule(null);
      loadInitialData();
    }
  };

  // 진행 상태 토글
  const handleToggleComplete = async () => {
    if (!selectedSchedule) return;
    const newStatus = !selectedSchedule.is_completed;
    try {
      const res = await crmDataService.toggleScheduleCompletion(selectedSchedule.id, newStatus);
      if (res.status === 'success') {
        setSelectedSchedule({ ...selectedSchedule, is_completed: newStatus });
        loadInitialData();
      }
    } catch (e) {
      alert("상태 변경에 실패했습니다.");
    }
  };

  // 즉시 피드 저장
  const handleQuickHistorySave = async (content: string) => {
    if (!content.trim() || !selectedSchedule) return;
    try {
      const res = await crmDataService.api_save_activity_log({
        deal_id: selectedSchedule.client_name,
        content: `[통합일정] ${content}`,
        editor: `[${selectedSchedule.team}] ${currentUser?.name || '관리자'}`
      });

      if (res.status === 'success' || res.ok) {
        alert("거래처 타임라인에 기록되었습니다.");
        const historyRes: any = await crmDataService.api_get_client_history_integrated('0', selectedSchedule.client_name);
        setClientHistory(historyRes.deal_history || []);
      }
    } catch (e) {
      alert("기록 저장 중 오류가 발생했습니다.");
    }
  };

  const handleSelectCompany = (contact: any) => {
    setFormData({ ...formData, client_id: contact.id, client_name: contact.company });
    setCompanySearch(contact.company);
    setShowCompanyList(false);
  };

  const openAddModal = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setFormData({
      id: null,
      team: '영업팀',
      assignee: currentUser?.name || '',
      client_id: null,
      client_name: '',
      schedule_date: dateStr,
      task_type: '',
      memo: ''
    });
    setCompanySearch('');
    setIsModalOpen(true);
  };

  // =============================================================================
  // [5] 렌더링 UI
  // =============================================================================
  return (
    <div className="flex h-[calc(100vh-140px)] gap-6 animate-fade-in font-sans">
      
      {/* 📅 좌측: 캘린더 메인 영역 (70%) */}
      <div className="flex-[7] bg-white rounded-[32px] shadow-sm border border-stone-200 flex flex-col overflow-hidden">
        {/* 달력 상단 컨트롤러 */}
        <div className="p-6 flex justify-between items-center border-b border-stone-100 bg-white/50 backdrop-blur-md">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-black text-stone-800 tracking-tight">
              {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
            </h2>
            <div className="flex bg-stone-100 p-1 rounded-xl border border-stone-200 shadow-inner">
              <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-white rounded-lg transition-all text-stone-600"><ChevronLeft size={18}/></button>
              <button onClick={() => setCurrentDate(new Date())} className="px-3 text-[11px] font-black text-stone-500 hover:text-stone-800 uppercase tracking-widest transition-colors">Today</button>
              <button onClick={() => changeMonth(1)} className="p-1.5 hover:bg-white rounded-lg transition-all text-stone-600"><ChevronRight size={18}/></button>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[10px] font-black uppercase">
            <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-sm"/> 영업팀</span>
            <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm"/> CS팀</span>
          </div>
        </div>

        {/* 요일 이름 헤더 */}
        <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-100">
          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((d, i) => (
            <div key={d} className={`py-3 text-center text-[10px] font-black tracking-widest ${i === 0 ? 'text-rose-500' : i === 6 ? 'text-blue-500' : 'text-stone-400'}`}>
              {d}
            </div>
          ))}
        </div>

        {/* 날짜 그리드 영역 */}
        <div className="flex-1 grid grid-cols-7 overflow-y-auto custom-scrollbar bg-stone-50/20">
          {calendarDays.map((day, idx) => {
            const dateStr = day ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : '';
            const daySchedules = schedules.filter(s => s.schedule_date === dateStr);
            const isToday = day && new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

            return (
              <div 
                key={idx} 
                onClick={() => day && setSelectedDay(day)}
                className={`min-h-[120px] border-b border-r border-stone-100 p-2 transition-all cursor-pointer hover:bg-white relative group
                  ${selectedDay === day ? 'bg-white shadow-[inset_0_0_0_2px_#e11d4820]' : ''} 
                  ${!day ? 'bg-stone-50/50' : 'bg-white'}`}
              >
                {day && (
                  <>
                    <div className="flex justify-between items-start mb-2">
                      <span className={`text-xs font-black transition-all ${isToday ? 'bg-rose-600 text-white w-6 h-6 flex items-center justify-center rounded-full shadow-lg shadow-rose-200' : 'text-stone-400'}`}>
                        {day}
                      </span>
                      <button 
                        onClick={(e) => { e.stopPropagation(); openAddModal(day); }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 bg-stone-900 text-white rounded-lg transition-all hover:scale-110 active:scale-95 shadow-md"
                      >
                        <Plus size={12} strokeWidth={4}/>
                      </button>
                    </div>
                    <div className="space-y-1.5">
                      {daySchedules.map(s => (
                        <div 
                          key={s.id}
                          onClick={(e) => { e.stopPropagation(); setSelectedSchedule(s); setSelectedDay(day); }}
                          className={`px-2 py-1.5 rounded-xl text-[10px] font-black truncate border shadow-sm transition-all hover:-translate-y-0.5 active:scale-95
                            ${s.is_completed ? 'opacity-40 grayscale line-through text-stone-400' : s.team === '영업팀' ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}
                        >
                          {s.is_completed && '✓ '}{s.client_name}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 📋 우측: 상세 정보 및 타임라인 (30% - 스크롤 및 공간 최적화) */}
      <div className="flex-[3] flex flex-col h-full overflow-hidden">
        <div className="flex-1 bg-white rounded-[32px] shadow-sm border border-stone-200 p-6 flex flex-col overflow-hidden">
          {selectedSchedule ? (
            <div className="flex flex-col h-full">
              {/* [상단 섹션] 업체 정보 및 메인 제어 - 고정 */}
              <div className="shrink-0 mb-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-black text-white uppercase tracking-widest ${selectedSchedule.team === '영업팀' ? 'bg-rose-500' : 'bg-emerald-500'}`}>
                        {selectedSchedule.team}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${selectedSchedule.is_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                        {selectedSchedule.is_completed ? 'COMPLETED' : 'PENDING'}
                      </span>
                    </div>
                    <h3 className="text-xl font-black text-stone-800 truncate tracking-tight">{selectedSchedule.client_name}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button 
                      onClick={() => { setFormData({...selectedSchedule}); setCompanySearch(selectedSchedule.client_name); setIsModalOpen(true); }}
                      className="p-2 text-stone-400 hover:text-stone-900 transition-all"
                      title="전체 수정"
                    >
                      <Edit3 size={18}/>
                    </button>
                    <button 
                      onClick={() => handleDelete(selectedSchedule.id)}
                      className="p-2 text-stone-300 hover:text-rose-600 transition-all"
                      title="일정 삭제"
                    >
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </div>

                {/* [상세 요약] 가로 배치로 세로 공간 절약 */}
                <div className="grid grid-cols-3 gap-2 p-3 bg-stone-50 rounded-2xl mb-4 border border-stone-100">
                  <div className="border-r border-stone-200 pr-1">
                    <p className="text-[9px] font-black text-stone-400 uppercase mb-0.5">담당 사원</p>
                    <p className="text-[11px] font-bold text-stone-700 truncate">{selectedSchedule.assignee}</p>
                  </div>
                  <div className="border-r border-stone-200 px-1">
                    <p className="text-[9px] font-black text-stone-400 uppercase mb-0.5">방문 일자</p>
                    <p className="text-[11px] font-bold text-stone-700 truncate">{selectedSchedule.schedule_date}</p>
                  </div>
                  <div className="pl-1">
                    <p className="text-[9px] font-black text-stone-400 uppercase mb-0.5">업무 내용</p>
                    <p className="text-[11px] font-bold text-stone-700 truncate">{selectedSchedule.task_type || '-'}</p>
                  </div>
                </div>

                {/* [진행 상태 및 공유 메모] 즉시 수정 기능 포함 */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2 px-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest">특이사항 메모</h4>
                      {!isEditingMemo && (
                        <button onClick={() => { setTempMemo(selectedSchedule.memo); setIsEditingMemo(true); }} className="p-1 text-stone-300 hover:text-rose-500 transition-colors">
                          <Edit3 size={12}/>
                        </button>
                      )}
                    </div>
                    <button 
                      onClick={handleToggleComplete}
                      className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all active:scale-95
                        ${selectedSchedule.is_completed ? 'bg-emerald-500 text-white' : 'bg-stone-200 text-stone-600 hover:bg-stone-300'}`}
                    >
                      {selectedSchedule.is_completed ? '✓ 완료됨' : '미완료'}
                    </button>
                  </div>

                  {isEditingMemo ? (
                    <div className="space-y-2">
                      <textarea 
                        value={tempMemo}
                        onChange={(e) => setTempMemo(e.target.value)}
                        className="w-full h-20 p-3 rounded-xl bg-white border-2 border-rose-200 text-[11px] text-stone-700 font-medium outline-none shadow-inner"
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setIsEditingMemo(false)} className="px-2 py-1 text-[10px] font-black text-stone-400 bg-stone-100 rounded-md">취소</button>
                        <button 
                          onClick={async () => {
                            const res = await crmDataService.saveSharedSchedule({ ...selectedSchedule, memo: tempMemo });
                            if (res.status === 'success') {
                              setSelectedSchedule({...selectedSchedule, memo: tempMemo});
                              setIsEditingMemo(false);
                              loadInitialData();
                            }
                          }}
                          className="px-2 py-1 text-[10px] font-black text-white bg-rose-600 rounded-md"
                        >
                          저장
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="p-3 bg-white border border-stone-100 rounded-xl text-[11px] text-stone-600 font-medium leading-relaxed min-h-[40px] cursor-pointer hover:border-stone-200"
                      onClick={() => { setTempMemo(selectedSchedule.memo); setIsEditingMemo(true); }}
                    >
                      {selectedSchedule.memo || '공유된 메모가 없습니다.'}
                    </div>
                  )}
                </div>

                {/* [입력창] 즉시 피드 기록 */}
                <div className="group">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <h4 className="text-[10px] font-black text-stone-800 uppercase tracking-wider">히스토리 기록</h4>
                    <span className="text-[9px] font-bold text-stone-300 italic">Enter로 저장</span>
                  </div>
                  <textarea 
                    placeholder="상담 내용을 적으면 타임라인에 남습니다."
                    className="w-full h-16 p-3 rounded-xl bg-stone-50 border-2 border-transparent focus:border-stone-200 focus:bg-white text-[11px] font-bold text-stone-700 outline-none resize-none transition-all placeholder:text-stone-300 shadow-inner"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleQuickHistorySave(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                </div>
              </div>

              {/* [하단 섹션] 거래처 히스토리 타임라인 - 스크롤 가능 구역 */}
              <div className="flex-1 flex flex-col min-h-0 border-t border-stone-100 pt-4 overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-[10px] font-black text-stone-800 flex items-center gap-2 uppercase tracking-wide">
                    <MessageSquare size={14} className="text-rose-500"/> 거래처 타임라인
                  </h4>
                  <span className="text-[9px] font-bold text-stone-400">{clientHistory.length} History</span>
                </div>
                <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                  {clientHistory.length > 0 ? (
                    clientHistory.map((h, i) => (
                      <div key={i} className="p-3 bg-stone-50/50 border border-stone-100 rounded-xl hover:bg-white hover:shadow-sm group transition-all">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[10px] font-black text-stone-800 group-hover:text-rose-600 transition-colors">{h.editor}</span>
                          <span className="text-[9px] font-bold text-stone-400">{h.created_at?.slice(5, 16)}</span>
                        </div>
                        <p className="text-[11px] text-stone-600 font-medium leading-relaxed">{h.content}</p>
                      </div>
                    ))
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-stone-300 py-10">
                      <MessageSquare size={32} className="mb-2 opacity-20"/>
                      <p className="text-[10px] font-bold">기록이 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-stone-400 text-center px-10">
              <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center mb-4 shadow-inner">
                <CalendarIcon size={32} className="text-stone-200"/>
              </div>
              <h3 className="text-md font-black text-stone-800 mb-1">일정을 선택하세요</h3>
              <p className="text-[11px] font-bold leading-relaxed text-stone-400">업무 정보와 타임라인을<br/>확인할 수 있습니다.</p>
            </div>
          )}
        </div>
      </div>

      {/* 🚀 [모달창] 일정 등록 및 수정 (주석 및 형식 100% 유지) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-stone-900/70 backdrop-blur-md z-[500] flex items-center justify-center p-4">
          <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in border border-white/20">
            {/* 모달 헤더 */}
            <div className="p-8 bg-stone-900 text-white flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-rose-600 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-900/20">
                  <Plus size={28} strokeWidth={3} />
                </div>
                <div>
                  <h3 className="text-xl font-black">일정 공유/수정</h3>
                  <p className="text-[11px] font-bold text-stone-400 uppercase tracking-widest">Schedule Management</p>
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-all active:scale-90"><X/></button>
            </div>
            
            {/* 모달 본문 */}
            <div className="p-8 space-y-6 bg-stone-50/30 overflow-y-auto max-h-[70vh]">
              {/* 팀 선택 */}
              <div className="flex p-1.5 bg-stone-200 rounded-[20px] shadow-inner">
                {['영업팀', 'CS팀'].map(t => (
                  <button 
                    key={t} 
                    onClick={() => setFormData({...formData, team: t})}
                    className={`flex-1 py-3.5 rounded-[16px] text-xs font-black transition-all duration-300
                      ${formData.team === t ? 'bg-white text-stone-900 shadow-md scale-[1.02]' : 'text-stone-500 hover:text-stone-700'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* 🔍 거래처 자동완성 */}
              <div className="space-y-2 relative" ref={companyListRef}>
                <label className="text-[11px] font-black text-stone-400 uppercase ml-1 tracking-wider">대상 거래처</label>
                <div className="relative group">
                  <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300 group-focus-within:text-stone-900 transition-colors"/>
                  <input 
                    type="text" 
                    value={companySearch} 
                    onChange={e => {
                      setCompanySearch(e.target.value);
                      setFormData({...formData, client_name: e.target.value, client_id: null});
                      setShowCompanyList(true);
                    }}
                    onFocus={() => setShowCompanyList(true)}
                    className="w-full pl-14 pr-6 py-4.5 rounded-[24px] border-2 border-stone-100 bg-white text-sm font-black focus:border-stone-900 outline-none transition-all" 
                    placeholder="업체명을 검색하거나 직접 입력하세요" 
                  />
                </div>
                {showCompanyList && companySearch && (
                  <div className="absolute top-full left-0 right-0 mt-3 bg-white rounded-[28px] shadow-2xl border border-stone-100 z-[10] max-h-60 overflow-y-auto animate-fade-in-up">
                    {contacts.filter(c => c.company.toLowerCase().includes(companySearch.toLowerCase())).length > 0 ? (
                      contacts.filter(c => c.company.toLowerCase().includes(companySearch.toLowerCase())).map(c => (
                        <div 
                          key={c.id} 
                          onClick={() => handleSelectCompany(c)}
                          className="p-5 hover:bg-stone-50 cursor-pointer flex justify-between items-center border-b border-stone-50 last:border-0 transition-colors"
                        >
                          <div>
                            <p className="text-sm font-black text-stone-800">{c.company}</p>
                            <p className="text-[10px] font-bold text-stone-400">{c.region || '정보 없음'}</p>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] font-black text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full">
                            <Check size={12} strokeWidth={4}/> 기존 고객
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-stone-400 font-bold text-xs">신규 거래처로 등록 (텍스트)</div>
                    )}
                  </div>
                )}
              </div>

              {/* 날짜 및 담당자 */}
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[11px] font-black text-stone-400 uppercase ml-1 tracking-wider">방문 예정일</label>
                  <input type="date" value={formData.schedule_date} onChange={e => setFormData({...formData, schedule_date: e.target.value})} className="w-full px-6 py-4.5 rounded-[24px] border-2 border-stone-100 bg-white text-sm font-black focus:border-stone-900 outline-none transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-black text-stone-400 uppercase ml-1 tracking-wider">담당 사원</label>
                  <select 
                    value={formData.assignee} 
                    onChange={e => setFormData({...formData, assignee: e.target.value})}
                    className="w-full px-6 py-4.5 rounded-[24px] border-2 border-stone-100 bg-white text-sm font-black focus:border-stone-900 outline-none appearance-none cursor-pointer"
                  >
                    <option value="">담당자 선택</option>
                    {users.map(u => (
                      <option key={u.id} value={u.name}>{u.name} ({u.team_name || '소속미상'})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ✍️ 업무 상세 내용 (텍스트 입력방식) */}
              <div className="space-y-2">
                <label className="text-[11px] font-black text-stone-400 uppercase ml-1 tracking-wider">업무 상세 내용</label>
                <input 
                  type="text" 
                  value={formData.task_type} 
                  onChange={e => setFormData({...formData, task_type: e.target.value})}
                  className="w-full px-6 py-4.5 rounded-[24px] border-2 border-stone-100 bg-white text-sm font-black focus:border-stone-900 outline-none transition-all" 
                  placeholder="예: 장비 시연 및 견적 전달" 
                />
              </div>

              {/* 공유 메모 */}
              <div className="space-y-2">
                <label className="text-[11px] font-black text-stone-400 uppercase ml-1 tracking-wider">공유 메모 (상세)</label>
                <textarea 
                  value={formData.memo} 
                  onChange={e => setFormData({...formData, memo: e.target.value})}
                  className="w-full h-32 px-6 py-5 rounded-[28px] border-2 border-stone-100 bg-white text-sm font-bold focus:border-stone-900 outline-none resize-none transition-all placeholder:text-stone-300" 
                  placeholder="팀원들에게 공유할 추가 내용을 적어주세요." 
                />
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className="p-8 bg-white flex gap-4 border-t border-stone-50">
              <button onClick={() => setIsModalOpen(false)} className="flex-1 py-5 rounded-[24px] font-black text-stone-500 bg-stone-100 hover:bg-stone-200 transition-all active:scale-95">취소</button>
              <button onClick={handleSave} className="flex-[2] py-5 rounded-[24px] font-black text-white bg-rose-600 hover:bg-rose-700 shadow-xl shadow-rose-200 transition-all active:scale-95 flex items-center justify-center gap-2"><Plus size={20} strokeWidth={3}/> 일정 저장 및 공유하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedCalendar;